import React, { useState } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import DataBase from "../../dataBase/liquidGuides";
import { applyTravelerPlan, summarizeTravelerPlan, travelerInfo } from "../../Common/travelerGuideCopy";
import { buildGuideSummary } from "../../Common/wanderlistSummary";

// Admin Dashboard → Guides: makes a Traveler guide — a cleaned COPY of an
// author's guide, owned by the signed-in (admin) account, marked
// type "Traveler" with status "draft". The author's guide is only read.
// Load the plan file (Traveler guides/<name>-plan.json), Check, Create.
// See Common/travelerGuideCopy.js. 2026-10-27.
export default function TravelerGuidePanel() {
  const user_info = useSelector((state) => state.user_info);
  const [plan, setPlan] = useState(null);
  const [source, setSource] = useState(null); // { info, data }
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState("");

  const load = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setSource(null);
    setConfirming(false);
    file.text().then((text) => {
      try {
        const p = JSON.parse(text);
        if (!p.source_guide_info_id) throw new Error("no source");
        setPlan(p);
        setMessage(`${file.name} loaded: “${p.guide_name}”.`);
      } catch (err) {
        setPlan(null);
        setMessage("That file isn't a Traveler guide plan.");
      }
    });
  };

  const check = () => {
    setBusy(true);
    setMessage("");
    DataBase.readGuideInfo(plan.source_guide_info_id, (ri) => {
      if (!ri.pass) {
        setBusy(false);
        setMessage("Could not read the author's guide.");
        return;
      }
      DataBase.readGuideData(ri.res.guide_id, (rd) => {
        setBusy(false);
        if (!rd.pass) {
          setMessage("Could not read the author's guide.");
          return;
        }
        const data = typeof rd.res.data === "string" ? JSON.parse(rd.res.data) : rd.res.data;
        const s = summarizeTravelerPlan(data, plan);
        setSource({ info: ri.res, data });
        setMessage(
          `“${ri.res.guide_name}” → “${plan.guide_name}”: places ${s.before.places} → ${s.after.places}, events ${s.before.events} → ${s.after.events}, ` +
            `schedules kept: ${s.schedules.join(", ")}; ${s.placesChanged} places and ${s.eventsChanged} events changed. The author's guide is not changed.`
        );
      });
    });
  };

  const create = () => {
    setConfirming(false);
    setBusy(true);
    const now = DateTime.utc().toISO();
    const data = { ...applyTravelerPlan(source.data, plan), user_id: user_info.uid };
    DataBase.addGuideData(data, (rd) => {
      if (!rd.pass) {
        setBusy(false);
        setMessage("Could not save the copy.");
        return;
      }
      const guide_id = rd.res;
      const info = { ...travelerInfo(source.info, plan, now), access: [user_info.uid] };
      try { info.wanderlist = buildGuideSummary(data); } catch (e) { delete info.wanderlist; }
      const owner = { user_id: user_info.uid, owner_name: `${user_info.first_name} ${user_info.last_name}`, guide_id };
      // the stored data carries its own id and card copy, like any saved guide
      DataBase.replaceGuideDataContent(guide_id, { ...data, guide_id, attributes: { ...info, ...owner } }, () => {
        DataBase.addGuideInfo(info, guide_id, user_info, (ri) => {
          setBusy(false);
          setMessage(ri.pass ? `Draft Traveler guide created: “${plan.guide_name}” (guide ${ri.res}). It's in My Guides.` : "The copy was saved but its guide card failed.");
          if (ri.pass) setSource(null);
        });
      });
    });
  };

  return (
    <div className="admindash-lookup">
      <p className="tai-paste-title admindash-lookup-title">Traveler guide: make a copy</p>
      <p className="tai-paste-hint">
        Makes a cleaned copy of an author's guide as a draft Traveler guide owned by this account. Load the plan file
        (Traveler guides/…-plan.json), Check, then Create. The author's guide is only read.
      </p>
      <div className="admindash-lookup-row">
        {/* file inputs are hidden app-wide (Modal.css); the label is the button */}
        <label className="tai-cancel-btn" style={{ margin: 0, cursor: "pointer" }}>
          Choose file…
          <input type="file" accept=".json,application/json" onChange={load} disabled={busy} aria-label="Plan file" />
        </label>
        <button className="tai-cancel-btn" onClick={check} disabled={busy || !plan}>
          {busy && !source ? "Checking…" : "Check"}
        </button>
        {source && !confirming && (
          <button className="tai-primary-btn" onClick={() => setConfirming(true)} disabled={busy}>
            {busy ? "Creating…" : "Create draft copy"}
          </button>
        )}
        {confirming && (
          <>
            <button className="tai-primary-btn" onClick={create}>
              Yes, create “{plan.guide_name}”
            </button>
            <button className="tai-cancel-btn" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </>
        )}
      </div>
      {message !== "" && <p className="tai-paste-hint">{message}</p>}
    </div>
  );
}
